import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Facebook, Building2, Receipt, Landmark } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

export default function Contact() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/contact-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  const c = info || {};

  return (
    <div data-testid="contact-page">
      <Seo title="Kontakt" description="Kontaktné údaje sekretariátu ObFZ Levice — adresa, e-mail, telefón, úradné hodiny a fakturačné údaje." />
      <PageHeader
        eyebrow="Sekretariát ObFZ"
        title="Kontakt"
        description="Kontaktné údaje sekretariátu, fakturačné údaje a sídlo Oblastného futbalového zväzu Levice."
        breadcrumbs={[{ label: "Kontakt" }]}
      />

      <section className="section-pad bg-paper">
        <div className="wrap">
          {/* MAIN CONTACT + MAP */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-ink-900">
            <div className="lg:col-span-5 p-8 md:p-12 bg-ink-900 text-white relative overflow-hidden">
              <div className="absolute bottom-0 right-0 stripe-accent w-40 h-40 opacity-15" />
              <div className="eyebrow-light mb-4">Sekretariát</div>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-white leading-none tracking-tight mb-8">
                Napíšte nám
              </h2>

              <ul className="space-y-6">
                <li>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Sídlo zväzu
                  </div>
                  <div className="text-white font-semibold">{c.organization || "Oblastný futbalový zväz Levice"}</div>
                  <div className="text-white/70 text-sm">{c.address || "Podjavorinskej 3, 934 01 Levice"}</div>
                </li>
                {c.secretary_name && (
                  <li>
                    <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Tajomníčka ObFZ</div>
                    <div className="text-white font-semibold">{c.secretary_name}</div>
                    {c.secretary_phone && (
                      <a href={`tel:${c.secretary_phone.replace(/\s+/g,"")}`} className="block text-white/80 font-mono text-sm hover:text-green-100 mt-0.5">
                        {c.secretary_phone}
                      </a>
                    )}
                  </li>
                )}
                <li>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Sekretariát
                  </div>
                  <a href={`mailto:${c.secretary_email}`} className="text-white font-mono text-lg hover:text-green-100">
                    {c.secretary_email}
                  </a>
                </li>
                <li>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Komisia rozhodcov
                  </div>
                  <a href={`mailto:${c.referee_email}`} className="text-white font-mono text-lg hover:text-green-100">
                    {c.referee_email}
                  </a>
                </li>
                <li>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Úradné hodiny
                  </div>
                  <div className="text-white text-sm">{c.office_hours}</div>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-white/20 flex flex-wrap gap-2">
                {c.futbalnet_url && (
                  <a href={c.futbalnet_url} target="_blank" rel="noopener noreferrer" className="btn btn-white">
                    Futbalnet <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
                {c.facebook_url && (
                  <a href={c.facebook_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-white">
                    <Facebook className="w-4 h-4" /> Facebook
                  </a>
                )}
              </div>
            </div>

            <div className="lg:col-span-7 min-h-[500px] bg-ink-100 relative">
              <iframe
                title="Mapa ObFZ Levice"
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
                src="https://www.openstreetmap.org/export/embed.html?bbox=18.5742%2C48.2098%2C18.6142%2C48.2298&amp;layer=mapnik&amp;marker=48.2198%2C18.5942"
              />
            </div>
          </div>

          {/* FAKTURAČNÉ ÚDAJE */}
          {c.ico && (
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-px bg-ink-100 border border-ink-100">
              <div className="lg:col-span-7 bg-white p-7 md:p-9">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b-2 border-ink-900">
                  <Receipt className="w-5 h-5 text-green-700" />
                  <h2 className="display-md">Fakturačné údaje</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">Názov</div>
                    <div className="font-heading font-bold text-ink-900">Oblastný futbalový zväz v Leviciach</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">Adresa</div>
                    <div className="text-ink-800">Podjavorinskej 3<br/>934 01 Levice</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">IČO</div>
                    <div className="font-mono font-semibold text-ink-900">{c.ico}</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">DIČ</div>
                    <div className="font-mono font-semibold text-ink-900">{c.dic}</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">Banka</div>
                    <div className="font-semibold text-ink-900 flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-green-700" /> {c.bank}
                    </div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">Číslo účtu</div>
                    <div className="font-mono font-semibold text-ink-900">{c.bank_account}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">IBAN</div>
                    <div className="font-mono font-bold text-green-700 text-lg tracking-wide bg-green-50 px-3 py-2 inline-block">
                      {c.iban}
                    </div>
                  </div>
                </div>

                {c.payment_note && (
                  <div className="mt-7 pt-5 border-t border-ink-100">
                    <div className="mono text-[10px] uppercase tracking-widest text-amber mb-2">Dôležité upozornenie</div>
                    <p className="text-sm text-ink-700 leading-relaxed">{c.payment_note}</p>
                  </div>
                )}
              </div>

              {/* Related FAs */}
              <div className="lg:col-span-5 bg-cream p-7 md:p-9">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b-2 border-ink-900">
                  <Building2 className="w-5 h-5 text-green-700" />
                  <h2 className="display-md">Súvisiace zväzy</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-green-700 mb-1.5">Regionálny zväz</div>
                    <div className="font-heading font-bold text-ink-900 text-base mb-1">Západoslovenský futbalový zväz</div>
                    <div className="text-sm text-ink-600 mb-2">Rázusova 23, 949 01 Nitra</div>
                    <a href="http://zsfz.sk" target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-ink-900 hover:text-green-700">
                      zsfz.sk <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="border-t border-ink-200 pt-5">
                    <div className="mono text-[10px] uppercase tracking-widest text-green-700 mb-1.5">Národný zväz</div>
                    <div className="font-heading font-bold text-ink-900 text-base mb-1">Slovenský futbalový zväz</div>
                    <div className="text-sm text-ink-600 mb-2">Tomášikova 30C, 821 01 Bratislava</div>
                    <a href="http://futbalsfz.sk" target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-ink-900 hover:text-green-700">
                      futbalsfz.sk <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
